import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ReportDetail } from '@/lib/content'

import Link from 'next/link'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ReadingProgress } from '@/components/reports/reading-progress'
import { ReportFigure } from '@/components/reports/report-figure'
import { ReportToc, sectionAnchor } from '@/components/reports/report-toc'
import { getReport, getReportSlugs } from '@/lib/content'
import { shouldRethrow } from '@/lib/db'
import {
    countWords,
    formatDate,
    pad2,
    readingMinutes
} from '@/lib/format'

interface ReportPageProps {
    detail: ReportDetail
}

const ReportPage: FC<ReportPageProps> = ({ detail }): ReactNode => {
    const { report, previous, next } = detail
    const words = report.sections.reduce((total, section) => total + countWords(section.body), 0)
    const minutes = readingMinutes(words)

    let figureCount = 0

    return (
        <Layout>
            <Seo title={`${report.code} ${report.title}`} description={report.abstract} figure={`Fig. 01 / Field report / ${report.code}`} shape='library' />

            <ReadingProgress targetId='report-body' />

            <article>
                <header data-ruler-mark='01' className='border-b border-hairline px-gutter pb-12 pt-12 desk:pb-16 desk:pt-16'>
                    <Link href='/field-reports' className='hud inline-flex items-center gap-2 text-mute transition-colors duration-300 hover:text-ink'>
                        <ArrowLeft className='size-4' strokeWidth={1.5} aria-hidden='true' />
                        All field reports
                    </Link>

                    <div className='mt-10 grid gap-10 desk:grid-cols-[260px_minmax(0,1fr)] desk:gap-16'>
                        <dl className='rise-fade grid content-start gap-y-3 self-start'>
                            <div className='border-t border-hairline pt-3'>
                                <dt className='hud text-mute'>
                                    Report
                                </dt>

                                <dd className='hud text-ink'>
                                    <span data-mdlfy-cms={`field_reports:${report.id}:code`}>
                                        {report.code}
                                    </span>
                                </dd>
                            </div>

                            <div className='border-t border-hairline pt-3'>
                                <dt className='hud text-mute'>
                                    Published
                                </dt>

                                <dd className='hud text-ink'>
                                    <time dateTime={report.publishedOn}>
                                        {formatDate(report.publishedOn)}
                                    </time>
                                </dd>
                            </div>

                            {report.authorId && report.authorName && (
                                <div className='border-t border-hairline pt-3'>
                                    <dt className='hud text-mute'>
                                        Written by
                                    </dt>

                                    <dd className='hud text-ink'>
                                        <span data-mdlfy-cms={`partners:${report.authorId}:name`}>
                                            {report.authorName}
                                        </span>
                                    </dd>
                                </div>
                            )}

                            <div className='border-t border-hairline pt-3'>
                                <dt className='hud text-mute'>
                                    Reading time
                                </dt>

                                <dd className='hud text-ink'>
                                    {pad2(minutes)} min / {words} words
                                </dd>
                            </div>
                        </dl>

                        <div>
                            <h1 className='display-lg max-w-[20ch]'>
                                <span className='rise-line'>
                                    <span className='rise-inner text-ink' data-mdlfy-cms={`field_reports:${report.id}:title`}>
                                        {report.title}
                                    </span>
                                </span>
                            </h1>

                            <p className='rise-fade mt-8 max-w-[52ch] font-heading text-[20px] leading-[1.45] tracking-[-0.01em] text-dim desk:text-[22px]' style={{ animationDelay: '200ms' }}>
                                <span data-mdlfy-cms={`field_reports:${report.id}:abstract`}>
                                    {report.abstract}
                                </span>
                            </p>
                        </div>
                    </div>
                </header>

                <div className='grid gap-12 px-gutter py-14 desk:grid-cols-[260px_minmax(0,1fr)] desk:gap-16 desk:py-20'>
                    <aside>
                        <ReportToc sections={report.sections} />
                    </aside>

                    <div id='report-body' className='max-w-[680px]'>
                        {report.sections.map((section, index) => {
                            const hasFigure = section.figureData !== null

                            if (hasFigure) figureCount += 1

                            return (
                                <section key={section.id} id={sectionAnchor(index)} aria-labelledby={`${sectionAnchor(index)}-title`} className='scroll-mt-32 border-t border-hairline pb-6 pt-8 first:border-t-0 first:pt-0'>
                                    <p className='hud text-mute'>
                                        Section {pad2(index + 1)}
                                    </p>

                                    <h2 id={`${sectionAnchor(index)}-title`} className='mt-3 font-heading text-[26px] font-medium leading-tight tracking-[-0.02em] text-ink desk:text-[30px]'>
                                        <span data-mdlfy-cms={`report_sections:${section.id}:heading`}>
                                            {section.heading}
                                        </span>
                                    </h2>

                                    <p className='mt-5 max-w-[62ch] text-[16px] leading-[1.75] text-ink/85' data-mdlfy-cms={`report_sections:${section.id}:body`}>
                                        {section.body}
                                    </p>

                                    {hasFigure && <ReportFigure section={section} reportCode={report.code} figureNumber={figureCount} />}
                                </section>
                            )
                        })}

                        {report.sections.length === 0 && (
                            <p className='hud border border-hairline px-gutter py-10 text-center text-mute'>
                                This report is being prepared for publication.
                            </p>
                        )}
                    </div>
                </div>
            </article>

            <nav aria-label='More field reports' className='grid border-y border-hairline desk:grid-cols-2'>
                {previous ? (
                    <Link href={`/field-reports/${previous.slug}`} className='group flex flex-col gap-5 border-b border-hairline px-gutter py-10 transition-colors duration-300 hover:bg-panel desk:border-b-0 desk:border-r desk:py-14'>
                        <span className='hud flex items-center gap-2 text-mute'>
                            <ArrowLeft className='size-4 transition-transform duration-500 ease-instrument group-hover:-translate-x-1' strokeWidth={1.5} aria-hidden='true' />
                            Earlier report /&nbsp;

                            <span data-mdlfy-cms={`field_reports:${previous.id}:code`}>
                                {previous.code}
                            </span>
                        </span>

                        <span className='display-md text-ink'>
                            <span data-mdlfy-cms={`field_reports:${previous.id}:title`}>
                                {previous.title}
                            </span>
                        </span>
                    </Link>
                ) : (
                    <div className='hidden border-r border-hairline desk:block' />
                )}

                {next ? (
                    <Link href={`/field-reports/${next.slug}`} className='group flex flex-col items-end gap-5 px-gutter py-10 text-right transition-colors duration-300 hover:bg-panel desk:py-14'>
                        <span className='hud flex items-center gap-2 text-mute'>
                            Newer report /&nbsp;

                            <span data-mdlfy-cms={`field_reports:${next.id}:code`}>
                                {next.code}
                            </span>

                            <ArrowRight className='size-4 transition-transform duration-500 ease-instrument group-hover:translate-x-1' strokeWidth={1.5} aria-hidden='true' />
                        </span>

                        <span className='display-md text-ink'>
                            <span data-mdlfy-cms={`field_reports:${next.id}:title`}>
                                {next.title}
                            </span>
                        </span>
                    </Link>
                ) : (
                    <Link href='/field-reports' className='group flex flex-col items-end justify-center gap-5 px-gutter py-10 text-right transition-colors duration-300 hover:bg-panel desk:py-14'>
                        <span className='hud flex items-center gap-2 text-mute'>
                            Latest report
                            <ArrowRight className='size-4' strokeWidth={1.5} aria-hidden='true' />
                        </span>

                        <span className='display-md text-ink'>
                            Back to the index
                        </span>
                    </Link>
                )}
            </nav>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const slugs = await getReportSlugs()

        return { paths: slugs.map(slug => ({ params: { slug } })), fallback: 'blocking' }
    }
    catch (error) {
        console.error('Report paths could not be loaded:', error instanceof Error ? error.message : error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<ReportPageProps, { slug: string }> = async ({ params }) => {
    const slug = params?.slug

    if (!slug) return { notFound: true }

    try {
        const detail = await getReport(slug)

        if (!detail) return { notFound: true, revalidate: 60 }

        return { props: { detail }, revalidate: 60 }
    }
    catch (error) {
        console.error(`Report ${slug} could not be loaded:`, error instanceof Error ? error.message : error)

        if (shouldRethrow()) throw error

        return { notFound: true, revalidate: 30 }
    }
}

export default ReportPage
